/**
 * Small presentational pieces shared by the five Troubleshooting tabs: cards,
 * pills, status dots, connection-kind chips, the "not connected yet" notice and
 * the ladder used by the Site-to-Site check. Palette and typography follow the
 * VPN debugger (vpnDebugConstants) so the page reads as one with the WireGuard
 * page it grew out of.
 */
import React from "react";
import {
  Box,
  Button,
  ButtonProps,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MONO,
  MULISH,
  PANEL_BORDER,
  SECTION_LABEL_SX,
} from "../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import {
  ConnectionKind,
  FindingOwner,
  GapResult,
} from "./troubleshootingInterfaces";
import { samplePreviewAvailable } from "./TroubleshootingUtils/apiEnvironment";

/** Cards and other panels inside the white page section. */
export const TS_SUBSECTION_BG = "#F4F6F8";

export const TS_COLORS = {
  navy: "#001943",
  orange: "#FF8400",
  orangeHover: "#E67300",
  ink2: "#4c5257",
  ink3: "#6b7075",
  line: "#d1d5db",
  green: "#1e7d34",
  greenTint: "#e6f4ea",
  red: "#b3261e",
  redTint: "#fdecec",
  amber: "#b25e00",
  amberTint: "#fff4e5",
  blue: "#283e6d",
  blueTint: "#eaf1fb",
  grey: "#5f6368",
  greyTint: "#eceff3",
  wg: "#0f766e",
  wgTint: "#d9f1ee",
  smtp: "#5b21b6",
  smtpTint: "#ede9fe",
} as const;

export type Tone =
  | "ok"
  | "bad"
  | "warn"
  | "info"
  | "grey"
  | "navy"
  | "wg"
  | "smtp"
  | "orange";

const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  ok: { bg: TS_COLORS.greenTint, fg: TS_COLORS.green },
  bad: { bg: TS_COLORS.redTint, fg: TS_COLORS.red },
  warn: { bg: TS_COLORS.amberTint, fg: TS_COLORS.amber },
  info: { bg: TS_COLORS.blueTint, fg: TS_COLORS.blue },
  grey: { bg: TS_COLORS.greyTint, fg: TS_COLORS.grey },
  navy: { bg: TS_COLORS.navy, fg: "#fff" },
  wg: { bg: TS_COLORS.wgTint, fg: TS_COLORS.wg },
  smtp: { bg: TS_COLORS.smtpTint, fg: TS_COLORS.smtp },
  orange: { bg: "#fdecd9", fg: "#9a4e0a" },
};

export const Pill = ({
  tone,
  children,
  title,
  sx,
}: {
  tone: Tone;
  children: React.ReactNode;
  title?: string;
  sx?: Record<string, unknown>;
}) => {
  const style = TONE_STYLE[tone];
  const pill = (
    <Box
      component="span"
      sx={{
        ...MULISH,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 11.5,
        fontWeight: 800,
        px: 1,
        py: "3px",
        borderRadius: "4px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
        backgroundColor: style.bg,
        color: style.fg,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill;
};

export const SampleChip = () => (
  <Pill
    tone="orange"
    title="Illustrative data from the design mockup. The backend endpoint for this surface does not exist yet."
  >
    Sample
  </Pill>
);

export const StatusDot = ({
  tone,
  label,
}: {
  tone: Tone;
  label: React.ReactNode;
}) => (
  <Box
    component="span"
    sx={{
      ...MULISH,
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      fontWeight: 700,
      fontSize: 14,
    }}
  >
    <Box
      component="span"
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
        backgroundColor: tone === "grey" ? "#b7bcc2" : TONE_STYLE[tone].fg,
      }}
    />
    {label}
  </Box>
);

export const KIND_LABEL: Record<ConnectionKind, string> = {
  wireguard: "WireGuard",
  "site-to-site": "Site-to-Site VPN (AWS)",
  direct: "Direct",
  smtp: "SMTP cameras",
  cloud: "Cloud VMS",
  unknown: "Unknown",
};

export const KindChip = ({ kind }: { kind: ConnectionKind }) => {
  if (kind === "wireguard") {
    return (
      <Pill tone="wg">
        <Box
          component="span"
          sx={{
            display: "inline-grid",
            placeItems: "center",
            width: 16,
            height: 16,
            borderRadius: "3px",
            backgroundColor: TS_COLORS.wg,
            color: "#fff",
            fontSize: 8,
          }}
        >
          WG
        </Box>
        WireGuard
      </Pill>
    );
  }
  if (kind === "site-to-site")
    return <Pill tone="info">{KIND_LABEL[kind]}</Pill>;
  if (kind === "smtp") return <Pill tone="smtp">{KIND_LABEL[kind]}</Pill>;
  return <Pill tone="grey">{KIND_LABEL[kind]}</Pill>;
};

export const OwnerPill = ({ owner }: { owner: FindingOwner }) => {
  if (owner === "none") return <Pill tone="grey">Not a fault</Pill>;
  if (owner === "self-serve")
    return <Pill tone="ok">You can fix this here, no ticket needed</Pill>;
  if (owner === "customer")
    return <Pill tone="orange">Fix is on your side</Pill>;
  if (owner === "actuate")
    return <Pill tone="info">Fix is on Actuate&rsquo;s side</Pill>;
  if (owner === "vms")
    return <Pill tone="grey">Fix is in your VMS or with its vendor</Pill>;
  return <Pill tone="grey">Shared: start on your side</Pill>;
};

export const Card = ({
  children,
  accent,
  sx,
  testId,
}: {
  children: React.ReactNode;
  /** Colour of a 3px top border; used for WireGuard and SMTP so their cards read as a family. */
  accent?: string;
  sx?: Record<string, unknown>;
  testId?: string;
}) => (
  <Paper
    elevation={0}
    data-testid={testId}
    sx={{
      border: PANEL_BORDER,
      borderTop: accent ? `3px solid ${accent}` : PANEL_BORDER,
      borderRadius: "6px",
      backgroundColor: TS_SUBSECTION_BG,
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

export const CardHeader = ({
  title,
  hint,
  right,
  leading,
}: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  right?: React.ReactNode;
  leading?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2.5,
      py: 1.5,
      borderBottom: PANEL_BORDER,
      flexWrap: "wrap",
    }}
  >
    {leading}
    <Typography
      component="h2"
      sx={{
        ...MULISH,
        fontWeight: 800,
        fontSize: 14,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: TS_COLORS.navy,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography
        component="span"
        sx={{
          ...MULISH,
          fontSize: 13,
          color: TS_COLORS.ink3,
          flex: "1 1 240px",
          minWidth: 0,
        }}
      >
        {hint}
      </Typography>
    )}
    {right && (
      <Box
        sx={{
          ml: "auto",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        {right}
      </Box>
    )}
  </Box>
);

export const Hint = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) => (
  <Typography
    component="span"
    sx={{ ...MULISH, fontSize: 12.5, color: TS_COLORS.ink3, ...sx }}
  >
    {children}
  </Typography>
);

export const Mono = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="code"
    sx={{
      ...MONO,
      fontSize: 13,
      backgroundColor: "#f0f1f3",
      px: 0.75,
      py: "1px",
      borderRadius: "3px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={SECTION_LABEL_SX}>{children}</Typography>
);

type TsButtonVariant = "primary" | "outline" | "quiet" | "ghost";

export const TsButton = ({
  variant = "primary",
  color,
  size = "md",
  children,
  sx,
  ...props
}: {
  variant?: TsButtonVariant;
  /** Override the accent, e.g. TS_COLORS.wg for WireGuard actions. */
  color?: string;
  size?: "sm" | "md";
  children: React.ReactNode;
} & Omit<ButtonProps, "variant" | "color" | "size">) => {
  const accent =
    color || (variant === "primary" ? TS_COLORS.orange : TS_COLORS.navy);
  const base = {
    ...MULISH,
    fontWeight: 700,
    fontSize: size === "sm" ? 13 : 14,
    textTransform: "none" as const,
    borderRadius: "4px",
    px: size === "sm" ? 1.5 : 2,
    py: size === "sm" ? 0.5 : 1,
    minWidth: 0,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  };
  const variants: Record<TsButtonVariant, Record<string, unknown>> = {
    primary: {
      backgroundColor: accent,
      color: "#fff",
      "&:hover": {
        backgroundColor: color ? accent : TS_COLORS.orangeHover,
        boxShadow: "none",
        filter: color ? "brightness(0.92)" : undefined,
      },
      "&.Mui-disabled": {
        backgroundColor: accent,
        color: "#fff",
        opacity: 0.45,
      },
    },
    outline: {
      backgroundColor: "#fff",
      color: accent,
      border: `1px solid ${accent}55`,
      "&:hover": { backgroundColor: `${accent}0d`, boxShadow: "none" },
    },
    quiet: {
      backgroundColor: "#fff",
      color: TS_COLORS.ink2,
      border: `1px solid ${TS_COLORS.line}`,
      "&:hover": { backgroundColor: "#f5f6f8", boxShadow: "none" },
    },
    ghost: {
      backgroundColor: "transparent",
      color: accent,
      px: 1,
      "&:hover": { backgroundColor: `${accent}0d`, boxShadow: "none" },
    },
  };
  return (
    <Button
      disableElevation
      sx={{ ...base, ...variants[variant], ...sx }}
      {...props}
    >
      {children}
    </Button>
  );
};

/**
 * What a surface shows when its backend does not exist yet. Says what is
 * missing in customer terms, and — for whoever is wiring it — names the call.
 */
export const GapNotice = ({
  result,
  what,
  kind = "load",
}: {
  result: Extract<GapResult<unknown>, { status: "unavailable" | "error" }>;
  /** Customer-facing noun: "Site-to-Site VPN status". */
  what: string;
  /**
   * "check" for a test the user ran (port check, credential test): it did
   * not run, or was refused. "load" for data a tab reads.
   */
  kind?: "load" | "check";
}) => {
  const unavailable = result.status === "unavailable";
  const refused = result.status === "error" && Boolean(result.refused);
  const pill = unavailable
    ? "Not connected yet"
    : refused
    ? "Not allowed"
    : kind === "check"
    ? "Didn't run"
    : "Could not load";
  const heading = unavailable
    ? `${what} isn't available in this environment yet.`
    : refused
    ? `${what} wasn't run.`
    : kind === "check"
    ? `${what} didn't run. Try again in a moment.`
    : `${what} could not be loaded.`;
  return (
    <Box
      role="status"
      sx={{
        m: 2.5,
        p: 2,
        border: `1px dashed ${TS_COLORS.line}`,
        borderRadius: "6px",
        backgroundColor: "#fafafb",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Pill tone={unavailable ? "grey" : "warn"}>{pill}</Pill>
        <Typography sx={{ ...MULISH, fontWeight: 700, fontSize: 14 }}>
          {heading}
        </Typography>
      </Box>
      <Typography
        sx={{
          ...MULISH,
          fontSize: 13,
          color: TS_COLORS.ink3,
          maxWidth: "90ch",
        }}
      >
        {unavailable ? result.reason : result.message}
      </Typography>
      {/* The endpoint is for us, not customers: only when a feature is not
          deployed in this environment, never on a refusal or failure. */}
      {unavailable && (
        <Typography sx={{ ...MONO, fontSize: 12, color: TS_COLORS.ink3 }}>
          Needs {result.endpoint}.
          {/* Only point at the switch where it is shown (K-9). */}
          {samplePreviewAvailable() &&
            " Turn on “Preview with sample data” to see the intended layout."}
        </Typography>
      )}
    </Box>
  );
};

export const EmptyCard = ({ children }: { children: React.ReactNode }) => (
  <Card>
    <Box sx={{ ...MULISH, p: 3, color: TS_COLORS.ink3, fontSize: 14 }}>
      {children}
    </Box>
  </Card>
);

/** One rung of a check ladder (used by the Site-to-Site connection check). */
export const LadderRung = ({
  index,
  title,
  status,
  detail,
  isLast,
}: {
  index: number;
  title: string;
  status: "pass" | "fail" | "warn" | "skipped" | "running" | "pending";
  detail: string;
  isLast: boolean;
}) => {
  const tone: Tone =
    status === "pass"
      ? "ok"
      : status === "fail"
      ? "bad"
      : status === "warn"
      ? "warn"
      : status === "running"
      ? "info"
      : "grey";
  const solid = status === "pass" || status === "fail" || status === "warn";
  const label = {
    pass: "Passed",
    fail: "Failed",
    warn: "Warning",
    skipped: "Skipped",
    running: "Running",
    pending: "Waiting",
  }[status];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "30px 1fr",
        gap: 1.75,
        position: "relative",
        py: 1.25,
      }}
    >
      {!isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 14,
            top: 42,
            bottom: -10,
            width: 2,
            backgroundColor: TS_COLORS.line,
          }}
        />
      )}
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          border: `2px solid ${
            solid
              ? TONE_STYLE[tone].fg
              : status === "running"
              ? TS_COLORS.blue
              : "#cfd3d8"
          }`,
          backgroundColor: solid
            ? TONE_STYLE[tone].fg
            : status === "skipped"
            ? "#f0f1f3"
            : "#fff",
          color: solid ? "#fff" : TS_COLORS.ink3,
          display: "grid",
          placeItems: "center",
          ...MULISH,
          fontWeight: 800,
          fontSize: 13,
          position: "relative",
          zIndex: 1,
        }}
      >
        {status === "pass" ? "✓" : status === "fail" ? "✕" : index}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            ...MULISH,
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {title}
          <Pill tone={tone}>{label}</Pill>
        </Box>
        <Typography
          sx={{
            ...MULISH,
            fontSize: 13.5,
            color: status === "fail" ? TS_COLORS.navy : TS_COLORS.ink3,
            mt: 0.25,
            maxWidth: "90ch",
          }}
        >
          {detail}
        </Typography>
      </Box>
    </Box>
  );
};

export const PORT_TONE: Record<"open" | "closed" | "filtered" | "error", Tone> =
  {
    open: "ok",
    closed: "warn",
    filtered: "bad",
    error: "grey",
  };

/**
 * What a port result is called. "filtered" is the wire value, but it is not a
 * verdict: a probe that got nothing back cannot tell a firewall drop from a
 * throttled return path (SUP-458, ENG-1838), so the UI says what it saw.
 */
export const PORT_LABEL: Record<
  "open" | "closed" | "filtered" | "error",
  string
> = {
  open: "Open",
  closed: "Closed",
  filtered: "No reply",
  error: "Error",
};

/** Table cell text style used across the tabs' MUI tables. */
export const CELL_SX = { ...MULISH, fontSize: 14, py: 1.5 } as const;
export const HEAD_CELL_SX = {
  ...MULISH,
  fontSize: 12.5,
  fontWeight: 800,
  color: TS_COLORS.ink2,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#fafafb",
} as const;

/** Shared shaded surface for emphasized table headers and debugger cards. */
export const SHADED_SURFACE_BG = "#E2E6EC";

/** Column headers that should read as a band, not another white strip. */
export const SHADED_HEAD_CELL_SX = {
  ...HEAD_CELL_SX,
  backgroundColor: SHADED_SURFACE_BG,
} as const;

export const siteAboutPath = (siteId: number) => `/sites/aboutsite/${siteId}`;
export const cameraEditPath = (siteId: number, cameraId: number) =>
  `/sites/${siteId}/editcamera/${cameraId}`;

const LINK_SX = {
  color: TS_COLORS.blue,
  fontWeight: 700,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

/**
 * A site name anywhere in the Troubleshooter opens that site's About page in
 * a new tab, so settings can be changed without losing the diagnosis. Both
 * pages read their ids from the URL, so a cold tab works.
 */
export const SiteLink = ({
  siteId,
  children,
}: {
  siteId: number;
  children: React.ReactNode;
}) => (
  <Box
    component="a"
    href={siteAboutPath(siteId)}
    target="_blank"
    rel="noopener noreferrer"
    title="Open the site page in a new tab"
    sx={LINK_SX}
  >
    {children}
  </Box>
);

/** A camera name opens that camera's settings page in a new tab. */
export const CameraLink = ({
  siteId,
  cameraId,
  children,
}: {
  siteId: number;
  cameraId: number;
  children: React.ReactNode;
}) => (
  <Box
    component="a"
    href={cameraEditPath(siteId, cameraId)}
    target="_blank"
    rel="noopener noreferrer"
    title="Open the camera settings in a new tab"
    sx={LINK_SX}
  >
    {children}
  </Box>
);

/**
 * `text` with the first occurrence of `siteName` rendered as a SiteLink, so a
 * sentence or title that names the site opens its page. Unchanged when the
 * name is not in the text.
 */
export const linkifySite = (
  text: string,
  siteId: number | null | undefined,
  siteName: string | null | undefined
): React.ReactNode => {
  if (siteId == null || !siteName) return text;
  const at = text.indexOf(siteName);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <SiteLink siteId={siteId}>{siteName}</SiteLink>
      {text.slice(at + siteName.length)}
    </>
  );
};
